import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "../data/config";

const DEFAULT_NAV = [
  { id: "home", label: "Home" },
  { id: "models", label: "Models" },
  { id: "compare", label: "Compare" },
  { id: "technology", label: "Technology" },
  { id: "about", label: "About" },
  { id: "dealer", label: "Dealer Network" },
  { id: "contact", label: "Contact" },
];

export const Header = ({ content, onScrollTo }) => {
  const nav = content?.navigation || DEFAULT_NAV;
  const registerLabel = content?.actions?.registerInterest || "Register Interest";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active section observer
  useEffect(() => {
    const ids = [...nav.map((n) => n.id), "register"];
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [nav]);

  const go = (id) => {
    setOpen(false);
    onScrollTo(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 glass border-b border-white/10 ${
        scrolled || open ? "bg-surface-deep/80" : "bg-surface-deep/55"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-[84px] sm:h-[104px] lg:h-[112px] flex items-center justify-between">
        <button
          onClick={() => go("home")}
          data-testid="logo-button"
          aria-label="evLUXE home"
          className="flex items-center"
        >
          <img src={LOGO_URL} alt="evLUXE logo" className="brand-logo-img h-[58px] sm:h-[73px] lg:h-[83px] w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {nav.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              data-testid={`nav-${n.id}`}
              className={`text-sm transition-colors relative ${
                active === n.id ? "text-brand" : "text-white/70 hover:text-white"
              }`}
            >
              {n.label}
              {active === n.id && (
                <span className="absolute -bottom-1.5 left-0 right-0 h-px bg-brand glow-brand" />
              )}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("register")}
            data-testid="header-register-cta"
            className="hidden sm:inline-flex items-center btn-glow bg-brand text-black font-semibold text-sm px-5 py-2.5 rounded-full"
          >
            {registerLabel}
          </button>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          className="lg:hidden glass border-t border-white/10 px-5 pb-6 pt-2"
          aria-label="Mobile"
          data-testid="mobile-nav"
        >
          {nav.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              data-testid={`mobile-nav-${n.id}`}
              className={`block w-full text-left py-3 border-b border-white/5 ${
                active === n.id ? "text-brand" : "text-white/80"
              }`}
            >
              {n.label}
            </button>
          ))}
          <button
            onClick={() => go("register")}
            className="mt-4 w-full bg-brand text-black font-semibold py-3 rounded-full"
            data-testid="mobile-register-cta"
          >
            {registerLabel}
          </button>
        </nav>
      )}
    </header>
  );
};

export default Header;
